"use client";

import { useState } from "react";
import {
  Calculator,
  Layers,
  Settings,
  ChevronRight,
  GripVertical,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { cn, formatNumber, formatCurrency } from "@/lib/utils";
import type { OutputField } from "@/types";

interface OutputGroup {
  name: string;
  fields: OutputField[];
}

interface ProposalSidebarProps {
  outputGroups: OutputGroup[];
  simulationOutputs: Record<string, unknown>;
  onAddOutput: (slug: string, value: unknown, label: string) => void;
  selectedElement: any | null;
  onElementUpdate?: (properties: Record<string, unknown>) => void;
  readOnly?: boolean;
}

export function ProposalSidebar({
  outputGroups,
  simulationOutputs,
  onAddOutput,
  selectedElement,
  onElementUpdate,
  readOnly = false,
}: ProposalSidebarProps) {
  const [searchTerm, setSearchTerm] = useState("");

  // Filter outputs based on search
  const filteredGroups = outputGroups.map((group) => ({
    ...group,
    fields: group.fields.filter(
      (field) =>
        field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        field.slug.toLowerCase().includes(searchTerm.toLowerCase())
    ),
  })).filter((group) => group.fields.length > 0);

  const formatOutputValue = (field: OutputField, value: unknown) => {
    if (value === null || value === undefined || value === "None") return "Aucun";
    
    switch (field.output_type) {
      case "currency":
        return formatCurrency(Number(value));
      case "percentage":
        return `${formatNumber(Number(value) * 100, field.decimal_places || 1)}%`;
      case "number":
        return formatNumber(Number(value), field.decimal_places || 2);
      case "boolean":
        return value ? "Oui" : "Non";
      default:
        return String(value);
    }
  };

  return (
    <div className="w-80 border-l bg-background flex flex-col h-full">
      <Tabs defaultValue="outputs" className="flex flex-col h-full">
        <TabsList className="grid w-full grid-cols-2 m-2">
          <TabsTrigger value="outputs" className="gap-2">
            <Calculator className="h-4 w-4" />
            Résultats
          </TabsTrigger>
          <TabsTrigger value="properties" className="gap-2">
            <Settings className="h-4 w-4" />
            Propriétés
          </TabsTrigger>
        </TabsList>

        <TabsContent value="outputs" className="flex-1 mt-0 overflow-hidden">
          <div className="p-3 border-b">
            <Input
              placeholder="Rechercher un résultat..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8"
            />
          </div>
          <ScrollArea className="flex-1 h-[calc(100vh-220px)]">
            <div className="p-3 space-y-2">
              {filteredGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Aucun résultat disponible
                </p>
              ) : (
                filteredGroups.map((group) => (
                  <OutputGroupSection
                    key={group.name}
                    group={group}
                    outputs={simulationOutputs}
                    onAddOutput={onAddOutput}
                    formatValue={formatOutputValue}
                    readOnly={readOnly}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="properties" className="flex-1 mt-0 overflow-hidden">
          <ScrollArea className="h-[calc(100vh-160px)]">
            <div className="p-3">
              {selectedElement ? (
                <ElementPropertiesPanel
                  element={selectedElement}
                  onUpdate={onElementUpdate}
                  readOnly={readOnly}
                />
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  <Layers className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    Sélectionnez un élément pour voir ses propriétés
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Output Group Component
interface OutputGroupSectionProps {
  group: OutputGroup;
  outputs: Record<string, unknown>;
  onAddOutput: (slug: string, value: unknown, label: string) => void;
  formatValue: (field: OutputField, value: unknown) => string;
  readOnly?: boolean;
}

function OutputGroupSection({
  group,
  outputs,
  onAddOutput,
  formatValue,
  readOnly,
}: OutputGroupSectionProps) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full p-2 rounded-md hover:bg-muted text-sm font-medium">
        <ChevronRight
          className={cn(
            "h-4 w-4 transition-transform",
            isOpen && "rotate-90"
          )}
        />
        {group.name}
        <Badge variant="secondary" className="ml-auto">
          {group.fields.length}
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-6 space-y-1 py-1">
          {group.fields.map((field) => {
            const value = outputs[field.slug];
            const formattedValue = formatValue(field, value);

            return (
              <div
                key={field.id}
                className={cn(
                  "flex items-center gap-2 p-2 rounded-md text-sm",
                  !readOnly && "hover:bg-muted group cursor-pointer"
                )}
                onClick={() => {
                  if (!readOnly) {
                    onAddOutput(field.slug, value, field.label);
                  }
                }}
                title={!readOnly ? "Cliquer pour ajouter au canvas" : undefined}
              >
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{field.label}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {field.slug}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-primary font-semibold">
                    {formattedValue}
                  </p>
                </div>
                {!readOnly && (
                  <Plus className="h-4 w-4 opacity-0 group-hover:opacity-50" />
                )}
              </div>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Element Properties Panel
interface ElementPropertiesPanelProps {
  element: any;
  onUpdate?: (properties: Record<string, unknown>) => void;
  readOnly?: boolean;
}

function ElementPropertiesPanel({
  element,
  onUpdate,
  readOnly,
}: ElementPropertiesPanelProps) {
  const elementType = element.customType || element.type || "unknown";

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium mb-2 capitalize">
          {elementType === "output_field"
            ? "Champ de sortie"
            : elementType === "textbox"
            ? "Texte"
            : elementType}
        </h3>
        <Separator />
      </div>

      {/* Position */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground uppercase">Position</Label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">X</Label>
            <Input
              type="number"
              value={Math.round(element.left || 0)}
              onChange={(e) =>
                onUpdate?.({ left: parseInt(e.target.value) || 0 })
              }
              disabled={readOnly}
              className="h-8"
            />
          </div>
          <div>
            <Label className="text-xs">Y</Label>
            <Input
              type="number"
              value={Math.round(element.top || 0)}
              onChange={(e) =>
                onUpdate?.({ top: parseInt(e.target.value) || 0 })
              }
              disabled={readOnly}
              className="h-8"
            />
          </div>
        </div>
      </div>

      {/* Size (for applicable elements) */}
      {(element.width !== undefined || element.height !== undefined) && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground uppercase">Taille</Label>
          <div className="grid grid-cols-2 gap-2">
            {element.width !== undefined && (
              <div>
                <Label className="text-xs">Largeur</Label>
                <Input
                  type="number"
                  value={Math.round(element.width || 0)}
                  onChange={(e) =>
                    onUpdate?.({ width: parseInt(e.target.value) || 0 })
                  }
                  disabled={readOnly}
                  className="h-8"
                />
              </div>
            )}
            {element.height !== undefined && (
              <div>
                <Label className="text-xs">Hauteur</Label>
                <Input
                  type="number"
                  value={Math.round(element.height || 0)}
                  onChange={(e) =>
                    onUpdate?.({ height: parseInt(e.target.value) || 0 })
                  }
                  disabled={readOnly}
                  className="h-8"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Text properties */}
      {(elementType === "textbox" || elementType === "output_field") && (
        <>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase">Texte</Label>
            <div>
              <Label className="text-xs">Taille de police</Label>
              <Input
                type="number"
                value={element.fontSize || 16}
                onChange={(e) =>
                  onUpdate?.({ fontSize: parseInt(e.target.value) || 16 })
                }
                disabled={readOnly}
                className="h-8"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Couleur</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={element.fill || "#000000"}
                onChange={(e) => onUpdate?.({ fill: e.target.value })}
                disabled={readOnly}
                className="w-12 h-8 p-0 border-0"
              />
              <Input
                value={element.fill || "#000000"}
                onChange={(e) => onUpdate?.({ fill: e.target.value })}
                disabled={readOnly}
                className="flex-1 h-8"
              />
            </div>
          </div>
        </>
      )}

      {/* Output field specific */}
      {elementType === "output_field" && (
        <div className="space-y-2 p-3 bg-muted rounded-md">
          <Label className="text-xs text-muted-foreground uppercase">
            Champ de sortie
          </Label>
          <div className="text-sm">
            <p>
              <span className="text-muted-foreground">Slug:</span>{" "}
              <code className="bg-background px-1 rounded">
                {element.outputSlug}
              </code>
            </p>
            <p>
              <span className="text-muted-foreground">Valeur originale:</span>{" "}
              <span className="font-mono">
                {String(element.originalValue ?? "-")}
              </span>
            </p>
            <p>
              <span className="text-muted-foreground">Éditable:</span>{" "}
              {element.isEditable ? "Oui" : "Non"}
            </p>
          </div>
        </div>
      )}

      {/* Shape properties */}
      {["rect", "circle", "triangle"].includes(elementType) && (
        <>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase">Apparence</Label>
            <div>
              <Label className="text-xs">Couleur de remplissage</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={element.fill || "#e5e5e5"}
                  onChange={(e) => onUpdate?.({ fill: e.target.value })}
                  disabled={readOnly}
                  className="w-12 h-8 p-0 border-0"
                />
                <Input
                  value={element.fill || "#e5e5e5"}
                  onChange={(e) => onUpdate?.({ fill: e.target.value })}
                  disabled={readOnly}
                  className="flex-1 h-8"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Couleur de bordure</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={element.stroke || "#000000"}
                  onChange={(e) => onUpdate?.({ stroke: e.target.value })}
                  disabled={readOnly}
                  className="w-12 h-8 p-0 border-0"
                />
                <Input
                  value={element.stroke || "#000000"}
                  onChange={(e) => onUpdate?.({ stroke: e.target.value })}
                  disabled={readOnly}
                  className="flex-1 h-8"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Épaisseur de bordure</Label>
              <Input
                type="number"
                value={element.strokeWidth || 1}
                onChange={(e) =>
                  onUpdate?.({ strokeWidth: parseInt(e.target.value) || 1 })
                }
                disabled={readOnly}
                className="h-8"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
